/**
 * Examination Chamber SEO.
 *
 * Two layers, one source, exactly like the portfolio:
 *   1. `useExamSeo` patches the live document for the CRA bundle.
 *   2. `scripts/prerender-exams.js` writes the same head and a crawlable body
 *      into a static file per diploma at build time.
 *
 * Everything here is derived from constants: no catalogue call happens at build
 * time, so the prerender never depends on a running backend.
 */
import { EXAM_CHAMBER_COPY as COPY } from '../../../constants/exam_messages.fr';
import { DIPLOMAS, EXAM_BASE_PATH, diplomaBrand, examDiplomaPath } from '../examChamberConstants';
import { appBrandText } from '../../../constants/brand';
import { SITE_ORIGIN, absoluteSiteUrl as absolute } from '../../../constants/site';
import { buildPageTags } from '../../../utils/documentSeo';
import { EXAM_PAPERS, examPaperPath, findExamPaper } from '../data/examPaperCatalog';

export const EXAM_SITE_ORIGIN = SITE_ORIGIN;

/** Minutes of the official paper, per diploma — the figure crawlers index. */
export const EXAM_DURATION_MINUTES = Object.freeze({
  brevet: 120, 'bac-premiere': 120, 'bac-terminale': 240,
});

export const EXAM_SEO_COPY = Object.freeze({
  HOME_TITLE: `Sujets du brevet et du bac en ligne | ${appBrandText('EXAM')}`,
  HOME_DESCRIPTION: 'Entraîne-toi sur de vrais sujets du brevet et du bac : épreuve chronométrée, note sur 20 et correction détaillée. Gratuit, sans inscription.',
  TITLE: {
    brevet: `Sujets du brevet de maths corrigés en ligne | ${appBrandText(diplomaBrand('brevet'))}`,
    'bac-premiere': `Sujets de maths de première corrigés | ${appBrandText(diplomaBrand('bac-premiere'))}`,
    'bac-terminale': `Annales du bac de maths corrigées | ${appBrandText(diplomaBrand('bac-terminale'))}`,
  },
  DESCRIPTION: {
    brevet: 'Entraîne-toi sur de vrais sujets du brevet de maths : épreuve chronométrée, note sur 20 et correction détaillée. Gratuit, sans inscription.',
    'bac-premiere': 'Passe un sujet de maths de première en 2 h : note sur 20, correction détaillée et priorités de révision. Gratuit, sans inscription.',
    'bac-terminale': 'Passe une annale du bac de maths en 4 h : note sur 20, correction détaillée et priorités de révision. Gratuit, sans inscription.',
  },
  EDUCATIONAL_LEVEL: {
    brevet: 'Classe de troisième', 'bac-premiere': 'Classe de première', 'bac-terminale': 'Classe de terminale',
  },
  BREADCRUMB_HOME: 'Accueil',
  BREADCRUMB_EXAMS: 'Examens',
  SUBJECT: 'Mathématiques',
  HEADING: {
    brevet: 'Sujets du brevet de maths : entraînement en ligne',
    'bac-premiere': 'Sujets de maths de première : entraînement en ligne',
    'bac-terminale': 'Annales du bac de maths : entraînement en ligne',
  },
});

const breadcrumb = (canonical, name) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: EXAM_SEO_COPY.BREADCRUMB_HOME, item: absolute('/') },
    { '@type': 'ListItem', position: 2, name: EXAM_SEO_COPY.BREADCRUMB_EXAMS, item: absolute(EXAM_BASE_PATH) },
    ...(name ? [{ '@type': 'ListItem', position: 3, name, item: canonical }] : []),
  ],
});

const paperBreadcrumb = (paper, canonical) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: EXAM_SEO_COPY.BREADCRUMB_HOME, item: absolute('/') },
    { '@type': 'ListItem', position: 2, name: EXAM_SEO_COPY.BREADCRUMB_EXAMS, item: absolute(EXAM_BASE_PATH) },
    {
      '@type': 'ListItem', position: 3, name: COPY.DIPLOMA_NAME[paper.diploma],
      item: absolute(examDiplomaPath(paper.diploma)),
    },
    { '@type': 'ListItem', position: 4, name: `${paper.label} ${paper.year}`, item: canonical },
  ],
});

const buildPaperMeta = (paper) => {
  const canonical = absolute(examPaperPath(paper));
  const diplomaName = COPY.DIPLOMA_NAME[paper.diploma];
  const shortName = paper.diploma === 'brevet' ? 'brevet de maths' : 'bac de maths';
  const qualifiedName = paper.diploma === 'brevet' ? 'du brevet de maths' : 'du bac de maths';
  const heading = `Sujet ${qualifiedName} ${paper.year} — ${paper.label}`;
  const description = `Consulte le sujet ${qualifiedName} ${paper.year} ${paper.code} : ${paper.exerciseCount} exercices et les chapitres abordés. Entraînement gratuit sur NeoProf.`;
  return {
    title: `Sujet ${shortName} ${paper.year} — ${paper.code} | ${appBrandText(diplomaBrand(paper.diploma))}`,
    description,
    canonical,
    heading,
    lead: `${paper.exerciseCount} exercices issus d’un sujet officiel, à travailler en conditions d’examen.`,
    ogImage: absolute('/og/formations/fr/neoprof-student-tools.png'),
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogImageAlt: `${heading} sur ${appBrandText(diplomaBrand(paper.diploma))}`,
    sections: [
      {
        heading: 'Chapitres abordés',
        body: paper.topics.join(', '),
      },
      {
        heading: 'S’entraîner en conditions réelles',
        body: `Prépare le ${diplomaName} avec une épreuve chronométrée, puis repère les notions à réviser en priorité.`,
      },
    ],
    paper,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'LearningResource',
        '@id': `${canonical}#paper`,
        name: heading,
        description,
        url: canonical,
        inLanguage: 'fr-FR',
        learningResourceType: 'Exam paper',
        educationalLevel: EXAM_SEO_COPY.EDUCATIONAL_LEVEL[paper.diploma],
        about: { '@type': 'Thing', name: EXAM_SEO_COPY.SUBJECT },
        isAccessibleForFree: true,
        provider: { '@type': 'Organization', name: 'NeoProf', url: EXAM_SITE_ORIGIN },
      },
      paperBreadcrumb(paper, canonical),
    ],
  };
};

/**
 * Everything both layers need for one exam route.
 *
 * @param {string} slug diploma slug, or '' for the exams landing
 * @returns {{ title, description, canonical, jsonLd, heading, lead }}
 */
export function buildExamMeta(slug = '') {
  const [diplomaSlug, year, paperSlug] = String(slug || '').split('/').filter(Boolean);
  if (diplomaSlug && year && paperSlug) {
    const paper = findExamPaper(diplomaSlug, year, paperSlug);
    if (paper) return buildPaperMeta(paper);
  }
  const diploma = DIPLOMAS.find((entry) => entry.slug === slug);
  if (!diploma) {
    const canonical = absolute(EXAM_BASE_PATH);
    return {
      title: EXAM_SEO_COPY.HOME_TITLE,
      description: EXAM_SEO_COPY.HOME_DESCRIPTION,
      canonical,
      heading: `${COPY.HOME_TITLE} ${COPY.HOME_PUNCH}`,
      lead: EXAM_SEO_COPY.HOME_DESCRIPTION,
      jsonLd: [breadcrumb(canonical, '')],
    };
  }
  const canonical = absolute(examDiplomaPath(diploma.slug));
  const name = COPY.DIPLOMA_NAME[diploma.slug];
  return {
    title: EXAM_SEO_COPY.TITLE[diploma.slug],
    description: EXAM_SEO_COPY.DESCRIPTION[diploma.slug],
    canonical,
    heading: EXAM_SEO_COPY.HEADING[diploma.slug],
    lead: COPY.GATE_LEAD[diploma.slug],
    ogImage: absolute('/og/formations/fr/neoprof-student-tools.png'),
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogImageAlt: `${name}, sujets de mathématiques sur ${appBrandText(diplomaBrand(diploma.slug))}`,
    sections: [
      {
        heading: `Préparer le ${name} en conditions réelles`,
        body: EXAM_SEO_COPY.DESCRIPTION[diploma.slug],
      },
      {
        heading: 'Une correction pour organiser tes révisions',
        body: 'À la fin de l’épreuve, consulte ta note, la correction question par question et les chapitres à retravailler en priorité.',
      },
    ],
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': ['LearningResource', 'Quiz'],
        '@id': `${canonical}#quiz`,
        name: EXAM_SEO_COPY.TITLE[diploma.slug],
        description: EXAM_SEO_COPY.DESCRIPTION[diploma.slug],
        url: canonical,
        inLanguage: 'fr-FR',
        learningResourceType: 'Exam',
        educationalLevel: EXAM_SEO_COPY.EDUCATIONAL_LEVEL[diploma.slug],
        about: { '@type': 'Thing', name: EXAM_SEO_COPY.SUBJECT },
        timeRequired: `PT${EXAM_DURATION_MINUTES[diploma.slug]}M`,
        isAccessibleForFree: true,
        provider: { '@type': 'Organization', name: 'NeoProf', url: EXAM_SITE_ORIGIN },
      },
      breadcrumb(canonical, name),
    ],
  };
}

/** Meta tags for one exam route, in the shape `applyDocumentSeo` consumes. */
export const buildExamTags = (meta) => buildPageTags(meta);

/** Every indexable exam URL — the sitemap's exam section. */
export const EXAM_SEO_ROUTES = Object.freeze([
  EXAM_BASE_PATH, ...DIPLOMAS.map((diploma) => examDiplomaPath(diploma.slug)),
  ...EXAM_PAPERS.map(examPaperPath),
]);
